import { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';

export function QrCameraScanner({ isOpen, onClose, onDetected }) {
  const videoRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  const onCloseRef = useRef(onClose);
  const [state, setState] = useState({ type: 'idle', message: '' });

  useEffect(() => {
    onDetectedRef.current = onDetected;
    onCloseRef.current = onClose;
  }, [onClose, onDetected]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let disposed = false;
    let qrScanner;
    let hasDetected = false;

    async function startCamera() {
      if (!globalThis.navigator?.mediaDevices?.getUserMedia) {
        setState({
          type: 'error',
          message: 'This browser does not support camera access.',
        });
        return;
      }

      setState({ type: 'pending', message: 'กำลังเปิดกล้อง…' });

      try {
        const { default: QrScanner } = await import('qr-scanner');
        if (disposed || !videoRef.current) {
          return;
        }

        qrScanner = new QrScanner(videoRef.current, async (result) => {
          if (!result || hasDetected || disposed) {
            return;
          }

          const value = String(result.data ?? result).trim();
          if (!value) {
            return;
          }

          hasDetected = true;
          setState({ type: 'pending', message: `พบ QR: ${value} กำลังบันทึก…` });

          try {
            await onDetectedRef.current(value);
            if (!disposed) {
              onCloseRef.current();
            }
          } catch (error) {
            hasDetected = false;
            setState({
              type: 'error',
              message: error instanceof Error ? error.message : 'ไม่สามารถบันทึก QR ได้',
            });
          }
        }, {
          preferredCamera: 'environment',
          maxScansPerSecond: 8,
          returnDetailedScanResult: true,
        });

        await qrScanner.start();

        if (disposed) {
          return;
        }

        setState({ type: 'success', message: 'เล็งกล้องไปที่ QR บนพาเลท' });
      } catch (error) {
        if (disposed) {
          return;
        }

        const permissionDenied = error?.name === 'NotAllowedError';
        setState({
          type: 'error',
          message: permissionDenied
            ? 'ไม่ได้รับอนุญาตให้ใช้กล้อง กรุณาอนุญาตกล้องในเบราว์เซอร์'
            : 'ไม่สามารถเปิดกล้องได้ กรุณาตรวจสอบกล้องและลองใหม่',
        });
      }
    }

    startCamera();

    return () => {
      disposed = true;
      qrScanner?.destroy();
      const stream = videoRef.current?.srcObject;
      stream?.getTracks?.().forEach((track) => track.stop());
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="kiosk-camera-backdrop" role="dialog" aria-modal="true" aria-label="สแกน QR ด้วยกล้อง">
      <div className="kiosk-camera-dialog">
        <button type="button" className="kiosk-camera-close" aria-label="ปิดกล้อง" onClick={onClose}>
          <X size={22} aria-hidden="true" />
        </button>
        <div className="kiosk-camera-heading">
          <Camera size={24} aria-hidden="true" />
          <h2>สแกน QR ด้วยกล้อง</h2>
        </div>
        <div className="kiosk-camera-preview">
          <video ref={videoRef} muted playsInline />
          <span className="kiosk-camera-target" aria-hidden="true" />
        </div>
        <p className={`kiosk-feedback kiosk-feedback-${state.type}`} role="status">
          {state.message}
        </p>
      </div>
    </div>
  );
}
